import React, { useEffect, useState } from "react";
import {
  ThinTextField,
  ThinDropdownWithSearch,
} from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const treatmentOptions = [
  "DHQ/THQ",
  "Private Hospital",
  "Rural Health Centre",
  "Basic Health Unit",
  "Private clinic",
  "Hakeem",
  "Traditional healer",
];

const femaleTreatmentOptions = [
  ...treatmentOptions,
  "Dai or traditional birth attendant",
  "Lady Health Worker",
  "Lady Health Visitor",
  "Relative / Friend / Neighbour",
];

const EditHealthStatus = ({ healthStatus: propHealthStatus, onUpdate, DqaApproved, DqaRejected }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [hs3, setHs3] = useState({});
  const [hs4, setHs4] = useState({});
  const [hs5, setHs5] = useState({
    givenBirth: "",
    antenatalCheckup: "",
    birthPlace: "",
  });

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [healthRecord, setHealthRecord] = useState(null);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected);

  // Location state
  const [districts, setDistricts] = useState([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState(null);

  // errors: { hs3: { [option]: 'message' }, hs4: { [option]: 'message' }, hs5: { field: 'message' } }
  const [errors, setErrors] = useState({ hs3: {}, hs4: {}, hs5: {} });

  const getDistrictNameById = (districtId) => {
    if (!districtId || !districts.length) return "";
    const id = parseInt(districtId);
    const district = districts.find((d) => parseInt(d.district_id) === id);
    return district ? district.district_name : "";
  };

  const getDistrictIdByName = (districtName) => {
    if (!districtName || !districts.length) return null;
    const district = districts.find((d) => d.district_name === districtName);
    return district ? parseInt(district.district_id) : null;
  };

  // Fetch location data
  const fetchLocationData = async () => {
    try {
      const districtRes = await fetchData("districts");
      const districtsData = districtRes?.data || [];
      setDistricts(districtsData);
      return districtsData;
    } catch (error) {
      console.error("Error loading location data:", error);
      toast.error("Error loading location data");
      return [];
    }
  };

  const sortedDistricts = districts.sort((a, b) => {
    if (a.district_id === selectedDistrictId) return -1;
    if (b.district_id === selectedDistrictId) return 1;
    return 0;
  });

  const fetchHouseholdInformation = async (householdInformationId) => {
    try {
      const response = await fetchData(
        `householdinformation/${householdInformationId}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/provinces"
      );

      const householdData = response?.data.household_information || {};
      
      const districtId = householdData?.district_id;
      if (districtId) {
        setSelectedDistrictId(districtId);
      }
    } catch (error) {
      console.error("Error loading household information:", error);
    }
  };

  // Initialize form from prop with proper district loading
  const initializeFormData = (healthData, districtsData = null) => {
    const availableDistricts = districtsData || districts;

    if (healthData && Object.keys(healthData).length > 0) {
      setHealthRecord(healthData);

      const mapYesNo = (v) => (v === 1 || v === true ? "yes" : "no");

      const locFromIdx = (idx) => {
        if (!idx || !availableDistricts.length) return "";
        const id = parseInt(idx);
        const district = availableDistricts.find(
          (d) => parseInt(d.district_id) === id
        );
        return district ? district.district_name : "";
      };

      const hs3Data = {
        "DHQ/THQ": {
          yesNo: mapYesNo(healthData.dhq_thq_used),
          location: locFromIdx(healthData.dhq_thq_location),
        },
        "Private Hospital": {
          yesNo: mapYesNo(healthData.private_hospital_used),
          location: locFromIdx(healthData.private_hospital_location),
        },
        "Rural Health Centre": {
          yesNo: mapYesNo(healthData.rural_health_center_used),
          location: locFromIdx(healthData.rural_health_center_location),
        },
        "Basic Health Unit": {
          yesNo: mapYesNo(healthData.basic_health_unit_used),
          location: locFromIdx(healthData.basic_health_unit_location),
        },
        "Private clinic": {
          yesNo: mapYesNo(healthData.private_clinic_used),
          location: locFromIdx(healthData.private_clinic_location),
        },
        Hakeem: {
          yesNo: mapYesNo(healthData.hakeem_used),
          location: locFromIdx(healthData.hakeem_location),
        },
        "Traditional healer": {
          yesNo: mapYesNo(healthData.traditional_healer_used),
          location: locFromIdx(healthData.traditional_healer_location),
        },
      };

      const hs4Data = {
        "DHQ/THQ": {
          yesNo: mapYesNo(healthData.female_dhq_thq_used),
          location: locFromIdx(healthData.female_dhq_thq_location),
        },
        "Private Hospital": {
          yesNo: mapYesNo(healthData.female_private_hospital_used),
          location: locFromIdx(healthData.female_private_hospital_location),
        },
        "Rural Health Centre": {
          yesNo: mapYesNo(healthData.female_rural_health_center_used),
          location: locFromIdx(healthData.female_rural_health_center_location),
        },
        "Basic Health Unit": {
          yesNo: mapYesNo(healthData.female_basic_health_unit_used),
          location: locFromIdx(healthData.female_basic_health_unit_location),
        },
        "Private clinic": {
          yesNo: mapYesNo(healthData.female_private_clinic_used),
          location: locFromIdx(healthData.female_private_clinic_location),
        },
        Hakeem: {
          yesNo: mapYesNo(healthData.female_hakeem_used),
          location: locFromIdx(healthData.female_hakeem_location),
        },
        "Traditional healer": {
          yesNo: mapYesNo(healthData.female_traditional_healer_used),
          location: locFromIdx(healthData.female_traditional_healer_location),
        },
        "Dai or traditional birth attendant": {
          yesNo: mapYesNo(healthData.female_dai_used),
          location: locFromIdx(healthData.female_dai_location),
        },
        "Lady Health Worker": {
          yesNo: mapYesNo(healthData.female_lhw_used),
          location: locFromIdx(healthData.female_lhw_location),
        },
        "Lady Health Visitor": {
          yesNo: mapYesNo(healthData.female_lhv_used),
          location: locFromIdx(healthData.female_lhv_location),
        },
        "Relative / Friend / Neighbour": {
          yesNo: mapYesNo(healthData.female_relative_used),
          location: locFromIdx(healthData.female_relative_location),
        },
      };

      setHs3(hs3Data);
      setHs4(hs4Data);
      setHs5({
        givenBirth: mapYesNo(healthData.women_birth_last_3_years),
        antenatalCheckup: mapYesNo(healthData.received_ante_natal_care),
        birthPlace: healthData.birth_location || "",
        birthPlaceOther:
          healthData.birth_location === "Others"
            ? healthData.birth_location_other_specify || ""
            : "",
      });
    } else {
      initializeEmptyForm();
    }
  };

  const initializeEmptyForm = () => {
    const emptyHs3 = {};
    const emptyHs4 = {};
    treatmentOptions.forEach(
      (o) => (emptyHs3[o] = { yesNo: "", location: "" })
    );
    femaleTreatmentOptions.forEach(
      (o) => (emptyHs4[o] = { yesNo: "", location: "" })
    );
    setHs3(emptyHs3);
    setHs4(emptyHs4);
    setHs5({ givenBirth: "", antenatalCheckup: "", birthPlace: "" });
    setHealthRecord(null);
  };

    useEffect(() => {
      setIsDqaApproved(DqaApproved);
      setIsDqaRejected(DqaRejected);
    }, [DqaApproved, DqaRejected]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load districts first
        const districtsData = await fetchLocationData();
        await fetchHouseholdInformation(id);

        // Initialize form with districts data available
        if (propHealthStatus && Object.keys(propHealthStatus).length > 0) {
          initializeFormData(propHealthStatus, districtsData);
        } else {
          initializeEmptyForm();
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Error loading data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, propHealthStatus]); // Only depend on id and propHealthStatus

  // Check if we have health status data
  const hasHealthStatusData =
    propHealthStatus && Object.keys(propHealthStatus).length > 0;

  // ---------- VALIDATION ----------
  const validate = () => {
    const e = { hs3: {}, hs4: {}, hs5: {} };

    // HS3 & HS4: if yes → location required
    Object.entries(hs3 || {}).forEach(([opt, val]) => {
      if (val?.yesNo === "yes" && !val?.location)
        e.hs3[opt] = "Location is required.";
    });
    Object.entries(hs4 || {}).forEach(([opt, val]) => {
      if (val?.yesNo === "yes" && !val?.location)
        e.hs4[opt] = "Location is required.";
    });

    // HS5: required givenBirth; if yes -> antenatal + birthPlace required
    if (hs5.givenBirth === "yes") {
      if (!hs5.antenatalCheckup)
        e.hs5.antenatalCheckup = "Please select an option.";
      if (!hs5.birthPlace) {
        e.hs5.birthPlace = "Please select birth place.";
      } else if (hs5.birthPlace === "Others" && !hs5.birthPlaceOther) {
        e.hs5.birthPlaceOther = " Other birth location is required.";
      }
    }

    setErrors(e);
    // Any error present?
    const hasErr =
      Object.keys(e.hs3).length ||
      Object.keys(e.hs4).length ||
      Object.keys(e.hs5).length;
    return !hasErr;
  };

  // Clear errors on change
  const clearHs3Err = (opt) =>
    setErrors((p) => ({ ...p, hs3: { ...p.hs3, [opt]: undefined } }));
  const clearHs4Err = (opt) =>
    setErrors((p) => ({ ...p, hs4: { ...p.hs4, [opt]: undefined } }));
  const clearHs5Err = (field) =>
    setErrors((p) => ({ ...p, hs5: { ...p.hs5, [field]: undefined } }));

  const handleTreatmentChange = (key, field, value, section) => {
    const setter = section === "hs3" ? setHs3 : setHs4;
    const state = section === "hs3" ? hs3 : hs4;
    setter({
      ...state,
      [key]: {
        ...state[key],
        [field]: value,
      },
    });
    section === "hs3" ? clearHs3Err(key) : clearHs4Err(key);
  };

  const handleUpdate = async () => {
    if (!validate()) {
      return;
    }

    try {
      setUpdating(true);

      const yn = (v) => (v === "yes" ? 1 : 0);

      // Helper function to get location ID only if yesNo is "yes"
      const getLocationId = (option, section) => {
        const sectionData = section === "hs3" ? hs3 : hs4;
        const yesNo = sectionData[option]?.yesNo;
        const location = sectionData[option]?.location;

        // Only return district ID if yesNo is "yes" AND location is provided
        if (yesNo === "yes" && location) {
          return getDistrictIdByName(location);
        }
        // Return null for "no" or empty location
        return null;
      };

      const payload = {
        householdHealthTreatmentLocations: {
          household_information_id: parseInt(id),

          // HS3
          dhq_thq_used: yn(hs3["DHQ/THQ"]?.yesNo),
          dhq_thq_location: getLocationId("DHQ/THQ", "hs3"),
          private_hospital_used: yn(hs3["Private Hospital"]?.yesNo),
          private_hospital_location: getLocationId("Private Hospital", "hs3"),
          rural_health_center_used: yn(hs3["Rural Health Centre"]?.yesNo),
          rural_health_center_location: getLocationId(
            "Rural Health Centre",
            "hs3"
          ),
          basic_health_unit_used: yn(hs3["Basic Health Unit"]?.yesNo),
          basic_health_unit_location: getLocationId("Basic Health Unit", "hs3"),
          private_clinic_used: yn(hs3["Private clinic"]?.yesNo),
          private_clinic_location: getLocationId("Private clinic", "hs3"),
          hakeem_used: yn(hs3["Hakeem"]?.yesNo),
          hakeem_location: getLocationId("Hakeem", "hs3"),
          traditional_healer_used: yn(hs3["Traditional healer"]?.yesNo),
          traditional_healer_location: getLocationId(
            "Traditional healer",
            "hs3"
          ),

          // HS4
          female_dhq_thq_used: yn(hs4["DHQ/THQ"]?.yesNo),
          female_dhq_thq_location: getLocationId("DHQ/THQ", "hs4"),
          female_private_hospital_used: yn(hs4["Private Hospital"]?.yesNo),
          female_private_hospital_location: getLocationId(
            "Private Hospital",
            "hs4"
          ),
          female_rural_health_center_used: yn(
            hs4["Rural Health Centre"]?.yesNo
          ),
          female_rural_health_center_location: getLocationId(
            "Rural Health Centre",
            "hs4"
          ),
          female_basic_health_unit_used: yn(hs4["Basic Health Unit"]?.yesNo),
          female_basic_health_unit_location: getLocationId(
            "Basic Health Unit",
            "hs4"
          ),
          female_private_clinic_used: yn(hs4["Private clinic"]?.yesNo),
          female_private_clinic_location: getLocationId(
            "Private clinic",
            "hs4"
          ),
          female_hakeem_used: yn(hs4["Hakeem"]?.yesNo),
          female_hakeem_location: getLocationId("Hakeem", "hs4"),
          female_traditional_healer_used: yn(hs4["Traditional healer"]?.yesNo),
          female_traditional_healer_location: getLocationId(
            "Traditional healer",
            "hs4"
          ),
          female_dai_used: yn(hs4["Dai or traditional birth attendant"]?.yesNo),
          female_dai_location: getLocationId(
            "Dai or traditional birth attendant",
            "hs4"
          ),
          female_lhw_used: yn(hs4["Lady Health Worker"]?.yesNo),
          female_lhw_location: getLocationId("Lady Health Worker", "hs4"),
          female_lhv_used: yn(hs4["Lady Health Visitor"]?.yesNo),
          female_lhv_location: getLocationId("Lady Health Visitor", "hs4"),
          female_relative_used: yn(hs4["Relative / Friend / Neighbour"]?.yesNo),
          female_relative_location: getLocationId(
            "Relative / Friend / Neighbour",
            "hs4"
          ),

          // HS5
          women_birth_last_3_years: yn(hs5.givenBirth),
          received_ante_natal_care:
            hs5.givenBirth === "yes" ? yn(hs5.antenatalCheckup) : 0,
          birth_location:
            hs5.givenBirth === "yes" ? hs5.birthPlace || null : null,
          birth_location_other_specify:
            hs5.givenBirth === "yes" && hs5.birthPlace === "Others"
              ? hs5.birthPlaceOther || null
              : null,
        },
      };

      if (healthRecord && healthRecord.household_health_treatment_location_id) {
        const response = await fetchData(
          `household-health-treatment-update/${healthRecord.household_health_treatment_location_id}`,
          "PUT",
          payload,
          {},
          {},
          navigate,
          "/household",
          dispatch
        );

        if (response?.success) {
          toast.success("Health status updated successfully");

          // Call onUpdate with the updated data
          if (onUpdate) {
            onUpdate(response.data);
          }
        } else {
          throw new Error(
            response?.message || "Failed to update health status"
          );
        }
      } else {
        toast.warning(
          "No existing health record found. Please create a record first."
        );
        return;
      }
    } catch (error) {
      console.error("Error updating health status:", error);
      toast.error(error.message || "Error updating health status");
    } finally {
      setUpdating(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="p-4 bg-white shadow rounded">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-2 text-gray-600">Loading health status...</p>
        </div>
      </div>
    );
  }

  const Err = (path, key) => {
    const msg = path === "hs5" ? errors?.hs5?.[key] : errors?.[path]?.[key];
    return msg ? (
      <p className="text-[0.8rem] text-red-600 mt-1">{msg}</p>
    ) : null;
  };

  return (
    <div className="bg-white rounded">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}

      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Health Status
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Health Status
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      <div className="mb-10">
        <h4 className="text-md font-medium mb-4">
          Where do you usually seek treatment for health issues?
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {treatmentOptions.map((option) => (
            <div key={option} className="mb-4">
              <p className="text-gray-700 font-medium mb-2">{option}</p>
              <div className="flex items-center gap-4 mb-2">
                {["yes", "no"].map((val) => (
                  <label key={val} className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`hs3-${option}`}
                      checked={hs3[option]?.yesNo === val}
                      onChange={() =>
                        handleTreatmentChange(option, "yesNo", val, "hs3")
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasHealthStatusData}
                    />
                    <span className="text-sm text-gray-700 capitalize">
                      {val}
                    </span>
                  </label>
                ))}
              </div>
              {hs3[option]?.yesNo === "yes" && (
                <div>
                  <ThinDropdownWithSearch
                    label="Location"
                    options={sortedDistricts.map((district) => ({
                      value: district.district_name,
                      label: district.district_name,
                    }))}
                    value={hs3[option]?.location || ""}
                    onChange={(value) =>
                      handleTreatmentChange(option, "location", value, "hs3")
                    }
                    disabled={isDqaApproved || isDqaRejected || !hasHealthStatusData}
                  />
                  {Err("hs3", option)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mb-10">
        <h4 className="text-md font-medium mb-4">
          Where does the female of the house seek advice/treatment for general
          illness or pregnancy related treatment?
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {femaleTreatmentOptions.map((option) => (
            <div key={option} className="mb-4">
              <p className="text-gray-700 font-medium mb-2">{option}</p>
              <div className="flex items-center gap-4 mb-2">
                {["yes", "no"].map((val) => (
                  <label key={val} className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`hs4-${option}`}
                      checked={hs4[option]?.yesNo === val}
                      onChange={() =>
                        handleTreatmentChange(option, "yesNo", val, "hs4")
                      }
                      className="accent-blue-600"
                      disabled={isDqaApproved || isDqaRejected || !hasHealthStatusData}
                    />
                    <span className="text-sm text-gray-700 capitalize">
                      {val}
                    </span>
                  </label>
                ))}
              </div>
              {hs4[option]?.yesNo === "yes" && (
                <div>
                  <ThinDropdownWithSearch
                    label="Location"
                    options={sortedDistricts.map((district) => ({
                      value: district.district_name,
                      label: district.district_name,
                    }))}
                    value={hs4[option]?.location || ""}
                    onChange={(value) =>
                      handleTreatmentChange(option, "location", value, "hs4")
                    }
                    disabled={isDqaApproved || isDqaRejected || !hasHealthStatusData}
                  />
                  {Err("hs4", option)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <h4 className="text-md font-medium mb-4">Birth Information</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Over the past 3 years, has any woman in the household given birth? *
          </label>
          <div className="flex items-center gap-6">
            {["yes", "no"].map((val) => (
              <label key={val} className="inline-flex items-center">
                <input
                  type="radio"
                  name="givenBirth"
                  value={val}
                  checked={hs5.givenBirth === val}
                  onChange={(e) => {
                    const value = e.target.value;
                    setHs5({
                      ...hs5,
                      givenBirth: value,
                      ...(value === "no"
                        ? { antenatalCheckup: "", birthPlace: "" }
                        : {}),
                    });
                    clearHs5Err("givenBirth");
                    if (value === "no") {
                      clearHs5Err("antenatalCheckup");
                      clearHs5Err("birthPlace");
                    }
                  }}
                  disabled={isDqaApproved || isDqaRejected || !hasHealthStatusData}
                />
                <span className="ml-2 text-sm text-gray-700 capitalize">
                  {val}
                </span>
              </label>
            ))}
          </div>
          {Err("hs5", "givenBirth")}
        </div>

        {hs5.givenBirth === "yes" && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Has she received any ante-natal check-ups?
              </label>
              <div className="flex items-center gap-6">
                {["yes", "no"].map((val) => (
                  <label key={val} className="inline-flex items-center">
                    <input
                      type="radio"
                      name="antenatalCheckup"
                      value={val}
                      checked={hs5.antenatalCheckup === val}
                      onChange={(e) => {
                        setHs5({ ...hs5, antenatalCheckup: e.target.value });
                        clearHs5Err("antenatalCheckup");
                      }}
                      disabled={isDqaApproved || isDqaRejected || !hasHealthStatusData}
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">
                      {val}
                    </span>
                  </label>
                ))}
              </div>
              {Err("hs5", "antenatalCheckup")}
            </div>

            <div>
              <ThinDropdownWithSearch
                label="Where did the mother give birth "
                options={[
                  "DHQ/THQ",
                  "Private Hospital",
                  "Rural Health Centre",
                  "Basic Health Unit",
                  "Private clinic",
                  "Home",
                  "Others",
                ].map((loc) => ({ value: loc, label: loc }))}
                value={hs5.birthPlace}
                onChange={(val) => {
                  setHs5({ ...hs5, birthPlace: val });
                  clearHs5Err("birthPlace");
                }}
                disabled={isDqaApproved || isDqaRejected || !hasHealthStatusData}
              />
              {Err("hs5", "birthPlace")}
            </div>
            {hs5.birthPlace === "Others" && (
              <div>
                <ThinTextField
                  label="Please specify other birth location"
                  value={hs5.birthPlaceOther || ""}
                  onChange={(e) => {
                    setHs5({ ...hs5, birthPlaceOther: e.target.value });
                    clearHs5Err("birthPlaceOther");
                  }}
                  disabled={isDqaApproved || isDqaRejected || !hasHealthStatusData}
                />
                {Err("hs5", "birthPlaceOther")}
              </div>
            )}
          </>
        )}
      </div>

      {/* Only show update button if we have data, not DQA approved, and have a health record */}
      {!isDqaApproved &&
        !isDqaRejected &&
        hasHealthStatusData &&
        healthRecord && (
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleUpdate}
              disabled={updating}
              className={`${
                updating ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
              } bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors`}
            >
              {updating ? "Updating..." : "Update"}
            </button>
          </div>
        )}
    </div>
  );
};

export default EditHealthStatus;
